<script>
import TerrainRaycastEngine from "./terrainRaycastEngine";

export default {
  name: "TerrainRaycastWorld",
  props: {
    locations: {
      type: Array,
      required: true
    },
    activeId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      engine: null,
      resizeObserver: null,
      renderHook: null,
      advanceHook: null,
      cameraState: {
        zoomPercent: 42,
        canZoomIn: true,
        canZoomOut: true
      },
      webglError: ""
    };
  },
  computed: {
    worldDescription() {
      const names = this.locations.map(location => location.label).join(", ");
      return `แผนที่เกาะสามมิติแบบ Three.js มีพื้นที่ ${names} ลากเพื่อหมุนกล้อง จีบนิ้วเพื่อซูม และแตะพื้นหรืออาคารเพื่อทดสอบ Raycasting`;
    }
  },
  watch: {
    activeId(nextId) {
      if (this.engine) this.engine.setSelected(nextId);
    }
  },
  mounted() {
    this.initializeEngine();
    window.addEventListener("keydown", this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.engine) this.engine.stop();
    if (window["render_game_to_text"] === this.renderHook) {
      delete window["render_game_to_text"];
    }
    if (window.advanceTime === this.advanceHook) {
      delete window.advanceTime;
    }
  },
  methods: {
    initializeEngine() {
      try {
        this.engine = new TerrainRaycastEngine(this.$refs.canvas, {
          locations: this.locations,
          onSelect: (id, point) => this.$emit("select", { id, point }),
          onTerrainPick: point => this.$emit("terrain-pick", point),
          onCameraChange: state => {
            this.cameraState = state;
          }
        });
        this.engine.setSelected(this.activeId, { immediate: true });

        this.renderHook = () => this.engine.renderGameToText();
        this.advanceHook = milliseconds =>
          this.engine.advanceTime(milliseconds);
        window["render_game_to_text"] = this.renderHook;
        window.advanceTime = this.advanceHook;

        this.observeSize();
        this.engine.start();
      } catch (error) {
        this.webglError =
          "อุปกรณ์นี้ยังเปิด WebGL ไม่สำเร็จ กรุณาลองเปิดด้วย LINE หรือ Safari/Chrome รุ่นล่าสุด";
        this.$emit("engine-error", error);
      }
    },
    observeSize() {
      if (typeof ResizeObserver !== "undefined") {
        this.resizeObserver = new ResizeObserver(entries => {
          const entry = entries[0];
          if (!entry || !this.engine) return;
          this.engine.resize(
            entry.contentRect.width,
            entry.contentRect.height,
            window.devicePixelRatio || 1
          );
        });
        this.resizeObserver.observe(this.$el);
      } else {
        window.addEventListener("resize", this.handleResize);
        this.handleResize();
      }
    },
    handleResize() {
      if (!this.engine) return;
      const rect = this.$el.getBoundingClientRect();
      this.engine.resize(rect.width, rect.height, window.devicePixelRatio || 1);
    },
    canvasRect(event) {
      return event.currentTarget.getBoundingClientRect();
    },
    handlePointerDown(event) {
      if (!this.engine) return;
      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      this.engine.handlePointerDown(
        event.clientX,
        event.clientY,
        this.canvasRect(event),
        event.pointerId
      );
    },
    handlePointerMove(event) {
      if (!this.engine) return;
      this.engine.handlePointerMove(
        event.clientX,
        event.clientY,
        this.canvasRect(event),
        event.pointerId
      );
    },
    handlePointerUp(event) {
      if (!this.engine) return;
      this.engine.handlePointerUp(
        event.clientX,
        event.clientY,
        this.canvasRect(event),
        event.pointerId
      );
      if (
        event.currentTarget.releasePointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    handlePointerCancel(event) {
      if (this.engine) this.engine.handlePointerCancel(event.pointerId);
      if (
        event.currentTarget.releasePointerCapture &&
        event.currentTarget.hasPointerCapture(event.pointerId)
      ) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    handleWheel(event) {
      if (this.engine) this.engine.handleWheel(event.deltaY);
    },
    handleKeydown(event) {
      const key = event.key.toLowerCase();
      if (key === "f") {
        this.toggleFullscreen();
      } else if (key === "+" || key === "=") {
        event.preventDefault();
        this.zoomIn();
      } else if (key === "-") {
        event.preventDefault();
        this.zoomOut();
      } else if (key === "0") {
        this.resetCamera();
      }
    },
    zoomIn() {
      if (this.engine) this.engine.zoomBy(-6);
    },
    zoomOut() {
      if (this.engine) this.engine.zoomBy(6);
    },
    resetCamera() {
      if (this.engine) this.engine.resetCamera();
    },
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (this.$el.requestFullscreen) {
        this.$el.requestFullscreen();
      }
    },
    selectAccessibleLocation(id) {
      if (this.engine) this.engine.setSelected(id);
      this.$emit("select", { id, point: null });
    }
  }
};
</script>

<template>
  <div class="terrain-world">
    <canvas
      ref="canvas"
      class="terrain-world__canvas"
      role="img"
      :aria-label="worldDescription"
      @pointerdown.prevent="handlePointerDown"
      @pointermove.prevent="handlePointerMove"
      @pointerup.prevent="handlePointerUp"
      @pointercancel="handlePointerCancel"
      @pointerleave="engine && engine.handlePointerLeave()"
      @wheel.prevent="handleWheel"
      @contextmenu.prevent
    ></canvas>

    <div v-if="webglError" class="terrain-world__error" role="alert">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <span>{{ webglError }}</span>
    </div>

    <aside class="terrain-world__camera" aria-label="ควบคุมกล้องสามมิติ">
      <button
        type="button"
        aria-label="ซูมเข้ากล้อง"
        :disabled="!cameraState.canZoomIn"
        @click="zoomIn"
      >
        <i class="fa fa-plus" aria-hidden="true"></i>
      </button>
      <output aria-live="polite">{{ cameraState.zoomPercent }}%</output>
      <button
        type="button"
        aria-label="ซูมออกกล้อง"
        :disabled="!cameraState.canZoomOut"
        @click="zoomOut"
      >
        <i class="fa fa-minus" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="terrain-world__reset"
        aria-label="คืนมุมกล้องเริ่มต้น"
        @click="resetCamera"
      >
        <i class="fa fa-home" aria-hidden="true"></i>
      </button>
    </aside>

    <nav class="terrain-world__accessible-nav" aria-label="เลือกอาคาร">
      <button
        v-for="location in locations"
        :key="location.id"
        type="button"
        @click="selectAccessibleLocation(location.id)"
      >
        {{ location.label }}
      </button>
    </nav>
  </div>
</template>

<style scoped>
.terrain-world {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #071c35;
  touch-action: none;
}

.terrain-world__canvas {
  display: block;
  width: 100%;
  height: 100%;
  outline: 0;
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-user-select: none;
}

.terrain-world__camera {
  position: absolute;
  top: 162px;
  right: 12px;
  z-index: 6;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 5px;
  border: 1px solid rgba(132, 231, 242, 0.28);
  border-radius: 19px;
  background: rgba(5, 21, 39, 0.72);
  box-shadow: 0 16px 38px rgba(1, 10, 25, 0.35);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.terrain-world__camera button {
  appearance: none;
  display: grid;
  place-items: center;
  width: 43px;
  height: 43px;
  padding: 0;
  color: #eefeff;
  border: 0;
  border-radius: 14px;
  background: rgba(111, 220, 234, 0.08);
  cursor: pointer;
}

.terrain-world__camera button:active {
  background: rgba(111, 220, 234, 0.22);
  transform: scale(0.94);
}

.terrain-world__camera button:disabled {
  cursor: default;
  opacity: 0.32;
}

.terrain-world__camera output {
  min-width: 48px;
  padding: 8px 0;
  color: #77f5e6;
  font-size: 11px;
  font-weight: 800;
  text-align: center;
}

.terrain-world__camera .terrain-world__reset {
  margin-top: 4px;
  color: #ffc870;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
}

.terrain-world__error {
  position: absolute;
  inset: 50% 22px auto;
  z-index: 12;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 18px;
  color: #ffffff;
  border: 1px solid rgba(255, 140, 111, 0.45);
  border-radius: 20px;
  background: rgba(44, 13, 30, 0.92);
  font-size: 14px;
  line-height: 1.5;
  transform: translateY(-50%);
}

.terrain-world__accessible-nav {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  clip-path: inset(50%);
}

@media (max-width: 420px) {
  .terrain-world__camera {
    top: 152px;
    right: 10px;
  }

  .terrain-world__camera button {
    width: 40px;
    height: 40px;
  }
}
</style>
